import { getAccessToken, useAuthStore } from '../stores/authStore';

/**
 * Paper/live portfolio REST client — talks to api-gateway `/api/portfolios/*`.
 *
 * Every request carries the user's JWT; api-gateway verifies it and forwards
 * `x-user-id` to strategy-engine, which scopes every lookup to that user.
 *
 * 4xx/5xx responses throw `Error(`${status}: ${body}`)`.
 */

export type MarketId = 'spot' | 'margin' | 'futures';

export type PortfolioConfig = {
  riskPerTradePct: number;
  maxPositionsPerSymbol: number;
  maxTotalPositions: number;
  slAtrMultiplier: number;
  tpAtrMultiplier: number;
  buyThreshold: number;
  sellThreshold: number;
  /** Per-position size cap (fraction of a strategy's slice). Default 0.20. */
  maxPositionPct?: number;
  /** Per-strategy RELATIVE capital weights (strategyId → weight). Normalized
   *  across enabled strategies; absent ⇒ equal split. */
  strategyWeights?: Record<string, number>;
};

export type PortfolioStats = {
  totalTrades: number;
  winRate: number;
  totalReturnPct: number;
  maxDrawdownPct: number;
};

export type Portfolio = {
  _id: string;
  userId: string;
  name: string;
  type: 'paper' | 'live';
  apiKeyId?: string | null;
  strategySymbols?: Record<string, string[]> | null;
  startingBalance: number;
  /** For live wallets this is enriched server-side with the real Kraken USD
      balance (a live wallet's stored value is seeded at 0). */
  currentBalance: number;
  /** Live wallets only: the real spendable Kraken USD balance, when the engine
      could fetch it. Absent when live is disabled or the fetch failed. */
  liveBalanceUsd?: number | null;
  strategies: string[];
  config: PortfolioConfig;
  stats: PortfolioStats;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Present for portfolios whose engine is currently loaded in strategy-engine.
   *  NOTE: the list endpoint intentionally OMITS closedTrades (it bloats the
   *  payload); fetch trade history via the per-wallet /trades endpoint. */
  liveSnapshot?: {
    balances: { spot: number; margin: number; futures: number };
    startingAllocations: { spot: number; margin: number; futures: number };
    peakEquity: number;
    positions: unknown[];
  } | null;
};

export type WalletTransfer = {
  _id: string;
  walletId: string;
  from: MarketId;
  to: MarketId;
  amount: number;
  status: 'pending' | 'complete' | 'failed';
  reason?: string;
  requestedAt: string;
  completedAt?: string;
  correlationId?: string;
};

async function authFetch<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body != null) init.body = JSON.stringify(opts.body);

  const res = await fetch(path, init);
  if (res.status === 401 && token) {
    const refreshRes = await fetch('/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (refreshRes.ok) {
      const { accessToken } = (await refreshRes.json()) as { accessToken: string };
      useAuthStore.getState().setAccessToken(accessToken);
      const retryHeaders = { ...headers, Authorization: `Bearer ${accessToken}` };
      const retryRes = await fetch(path, { ...init, headers: retryHeaders });
      if (!retryRes.ok) throw new Error(`${retryRes.status}: ${await retryRes.text()}`);
      return retryRes.json() as Promise<T>;
    }
  }
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

export function listPortfolios(): Promise<{ portfolios: Portfolio[] }> {
  return authFetch('/api/portfolios');
}

/** Global live-trading armed state — drives the honest live banner. */
export type LiveStatus = {
  /** True when real Kraken orders can be placed (dry-run off AND kill released). */
  armed: boolean;
  /** Kraken client in validate-only mode (no real orders). */
  dryRun: boolean;
  /** Kill switch engaged (env kill OR admin liveTrading flag off). */
  killed: boolean;
};

export function fetchLiveStatus(): Promise<LiveStatus> {
  return authFetch('/api/live-status');
}

export function createPortfolio(args: {
  name: string;
  startingBalance: number;
  strategies?: string[];
  config?: Partial<PortfolioConfig>;
}): Promise<{ portfolio: Portfolio }> {
  return authFetch('/api/portfolios', { method: 'POST', body: args });
}

export function deletePortfolio(id: string): Promise<{ deleted: true; portfolioId: string }> {
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type CostBasisResult = {
  position: {
    _id: string;
    entryPrice: number;
    entrySource: 'kraken-vwap' | 'manual';
    qty?: number;
    buyFills?: number;
  };
};

/** Set a manual position's cost basis to an operator-typed entry price. */
export function setManualCostBasis(
  portfolioId: string,
  positionId: string,
  entryPrice: number,
): Promise<CostBasisResult> {
  return authFetch(
    `/api/portfolios/${encodeURIComponent(portfolioId)}/positions/${encodeURIComponent(positionId)}/cost-basis`,
    { method: 'PATCH', body: { entryPrice } },
  );
}

/** Thrown by recomputeCostBasis when Kraken has no buy fills for the asset
 *  (e.g. it was deposited/transferred in) — the caller should prompt for a
 *  manual cost basis instead of treating it as a generic failure. */
export class NoBuyFillsError extends Error {
  constructor() {
    super('No Kraken buy fills found for this asset — set the cost basis manually.');
    this.name = 'NoBuyFillsError';
  }
}

/** Reconstruct a manual position's cost basis as the volume-weighted average of
 *  the user's Kraken fills. Throws NoBuyFillsError (HTTP 422) when none exist. */
export async function recomputeCostBasis(
  portfolioId: string,
  positionId: string,
): Promise<CostBasisResult> {
  const token = getAccessToken();
  const path = `/api/portfolios/${encodeURIComponent(portfolioId)}/positions/${encodeURIComponent(positionId)}/cost-basis/recompute`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(path, { method: 'POST', headers, credentials: 'include' });
  if (res.status === 422) throw new NoBuyFillsError();
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json() as Promise<CostBasisResult>;
}

export function patchPortfolio(
  id: string,
  body: {
    name?: string;
    strategies?: string[];
    config?: Partial<PortfolioConfig>;
    strategySymbols?: Record<string, string[]>;
  },
): Promise<{ portfolio: Portfolio }> {
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export function fetchPortfolioSnapshot(id: string): Promise<unknown> {
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}/snapshot`);
}

export function fetchPortfolioHistory(
  id: string,
  query: { from?: string; to?: string; interval?: string } = {},
): Promise<PortfolioHistoryResponse> {
  const qs = new URLSearchParams();
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  if (query.interval) qs.set('interval', query.interval);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}/history${suffix}`);
}

export type PortfolioTradesQuery = {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  action?: 'open' | 'close' | 'all';
  /** Currently-open positions only (open leg with no matching close). */
  open?: boolean;
  symbol?: string;
};

/**
 * Build the `?...` suffix for the portfolio trades endpoint. Exported so the
 * param mapping is unit-testable without a network round-trip.
 *
 * `open=true` (currently-open positions) takes precedence over `action`, since
 * "open positions" is a server-side join, not the raw `action='open'` leg.
 */
export function buildPortfolioTradesQuery(query: PortfolioTradesQuery): string {
  const qs = new URLSearchParams();
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  if (query.limit != null) qs.set('limit', String(query.limit));
  if (query.offset != null) qs.set('offset', String(query.offset));
  if (query.open) qs.set('open', 'true');
  else if (query.action && query.action !== 'all') qs.set('action', query.action);
  if (query.symbol) qs.set('symbol', query.symbol);
  return qs.toString() ? `?${qs.toString()}` : '';
}

export function fetchPortfolioTrades(
  id: string,
  query: PortfolioTradesQuery = {},
): Promise<TradesResponse> {
  const suffix = buildPortfolioTradesQuery(query);
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}/trades${suffix}`);
}

export type PortfolioTradeAnalytics = {
  summary: {
    total_trades: string;
    wins: string;
    losses: string;
    total_pnl: string;
    avg_win: string;
    avg_loss: string;
    best_trade: string;
    worst_trade: string;
  } | null;
  bySymbol: Array<{ symbol: string; trades: string; pnl: string }>;
  byStrategy: Array<{ strategy_id: string; trades: string; pnl: string }>;
  monthly: Array<{ month: string; trades: string; pnl: string }>;
};

export function fetchPortfolioTradeAnalytics(
  id: string,
  query: { from?: string; to?: string } = {},
): Promise<PortfolioTradeAnalytics> {
  const qs = new URLSearchParams();
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return authFetch(
    `/api/portfolios/${encodeURIComponent(id)}/trades/analytics${suffix}`,
  );
}

export function transferBuckets(
  id: string,
  args: { from: MarketId; to: MarketId; amount: number },
): Promise<{
  portfolioId: string;
  from: MarketId;
  to: MarketId;
  amount: number;
  balances: { spot: number; margin: number; futures: number };
}> {
  return authFetch(`/api/portfolios/${encodeURIComponent(id)}/transfer`, {
    method: 'POST',
    body: args,
  });
}

export function listTransfers(
  id: string,
  limit = 50,
): Promise<{ portfolioId: string; transfers: WalletTransfer[] }> {
  return authFetch(
    `/api/portfolios/${encodeURIComponent(id)}/transfers?limit=${encodeURIComponent(String(limit))}`,
  );
}

// ── Portfolio Presets ────────────────────────────────────────

export type PresetAllocation = {
  strategyTemplate: 'grid' | 'dca' | 'momentum' | 'mean-reversion';
  symbolSelection: 'top-N-by-marketcap' | 'user-picks' | 'stable-only';
  symbolCount: number;
  weight: number;
  paramOverrides: Record<string, unknown>;
};

export type PortfolioPreset = {
  _id: string;
  name: string;
  market: MarketId;
  description: string;
  targetVolatility: number;
  allocations: PresetAllocation[];
  version: number;
};

export function listPresets(): Promise<{ presets: PortfolioPreset[] }> {
  return authFetch('/api/presets');
}

export function getPreset(id: string): Promise<PortfolioPreset> {
  return authFetch(`/api/presets/${encodeURIComponent(id)}`);
}

export type ApplyPresetConflict = {
  code: 'active-strategies-present';
  error: string;
  activeStrategies: Array<{ _id: string; name: string }>;
};

export type ApplyPresetSuccess = {
  portfolioId: string;
  presetId: string;
  presetName: string;
  disabledCount: number;
  allocationsQueued: number;
  message: string;
};

export async function applyPreset(args: {
  portfolioId: string;
  presetId: string;
  confirm?: boolean;
}): Promise<ApplyPresetSuccess | ApplyPresetConflict> {
  const path = `/api/portfolios/${encodeURIComponent(args.portfolioId)}/preset`;
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(path, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ presetId: args.presetId, confirm: args.confirm }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 202) return body as ApplyPresetSuccess;
  if (res.status === 409 && body?.code === 'active-strategies-present') {
    return body as ApplyPresetConflict;
  }
  throw new Error((body as { error?: string })?.error ?? `${res.status}`);
}

// ── Trade / analytics / history record shapes ───────────────
// Migrated from the retired singleton `tradingApi.ts`. These describe the
// rows returned by the portfolio-scoped trade + history endpoints above and
// are consumed by the trade journal, equity curve, and analytics widgets.

export type TradeRow = {
  time: string;
  symbol: string;
  trade_id: string;
  strategy_id: string;
  action: 'open' | 'close';
  side: 'long' | 'short';
  entry_price: string;
  exit_price: string | null;
  exit_reason: string | null;
  position_size: string;
  position_qty: string;
  realized_pnl: string | null;
  realized_pnl_pct: string | null;
  stop_loss_price: string | null;
  take_profit_price: string | null;
  opened_at: string;
  closed_at: string | null;
  /** True for operator-held MANUAL trades (scopes the trade history to the
   *  Manual sleeve). Absent on rows that predate the column. */
  manual?: boolean;
};

export type TradesResponse = {
  trades: TradeRow[];
  total: number;
  limit: number;
  offset: number;
};

export type TradeSummary = {
  total_trades: string;
  wins: string;
  losses: string;
  total_pnl: string;
  avg_win: string;
  avg_loss: string;
  best_trade: string;
  worst_trade: string;
};

export type AnalyticsResponse = {
  summary: TradeSummary | null;
  bySymbol: Array<{ symbol: string; trades: string; pnl: string }>;
  byStrategy: Array<{ strategy_id: string; trades: string; pnl: string }>;
  monthly: Array<{ month: string; trades: string; pnl: string }>;
};

export type PortfolioSnapshot = {
  bucket: string;
  total_equity: string;
  cash_balance: string;
  deployed_capital: string;
  unrealized_pnl: string;
  realized_pnl: string;
  max_drawdown_pct: string;
  open_positions: number;
  win_count: number;
  loss_count: number;
  // ── Equity composition (live wallets; derived server-side from the snapshot's
  //    positions jsonb). Present on the /history response so the Overall equity
  //    curve can draw Total + Strategy + Cash + Manual lines. ──
  /** Free cash (= cash_balance), as a number-string. */
  cash?: string;
  /** Σ market value of MANUAL (operator-held) positions. */
  manual_value?: string;
  /** Σ market value of STRATEGY (engine-managed) positions. */
  strategy_value?: string;
};

export type PortfolioHistoryResponse = {
  snapshots: PortfolioSnapshot[];
  interval: string;
};

// ── Manual paper trade (chart ticket) ───────────────────────

export type OpenManualTradeRequest = {
  symbol: string;
  side: 'long' | 'short';
  entryPrice: number;
  stopLossPrice: number;
  takeProfitPrice: number;
  /** Either `positionQty` (base asset) or `notional` (quote asset) is required. */
  positionQty?: number;
  notional?: number;
};

export type OpenManualTradeResponse = {
  tradeId: string;
  event: unknown;
};

/**
 * Open a user-submitted paper trade with explicit SL/TP against a specific
 * portfolio. Server-side the strategy-engine's `openManualTrade` validates
 * balance + SL/TP direction and publishes a `paper-trade` event via Kafka so
 * WS subscribers update.
 */
export function openManualPaperTrade(
  portfolioId: string,
  req: OpenManualTradeRequest,
): Promise<OpenManualTradeResponse> {
  return authFetch(
    `/api/portfolios/${encodeURIComponent(portfolioId)}/paper/open`,
    { method: 'POST', body: req },
  );
}

export type CloseManualTradeRequest = {
  tradeId: string;
  /** Optional partial close — defaults to the full position. */
  qty?: number;
};

export type CloseManualTradeResponse = {
  tradeId: string;
  closed: boolean;
  /** Remaining qty after a partial close. 0 when fully closed. */
  remainingQty: number;
};

/**
 * Close (or reduce) an existing paper position. Used by the chart Sell
 * button on SPOT — a Sell there means "close what you own", not "open a
 * short". MARGIN / FUTURES uses `openManualPaperTrade` with `side: 'short'`.
 */
export function closeManualPaperTrade(
  portfolioId: string,
  req: CloseManualTradeRequest,
): Promise<CloseManualTradeResponse> {
  return authFetch(
    `/api/portfolios/${encodeURIComponent(portfolioId)}/paper/close`,
    { method: 'POST', body: req },
  );
}

// ── Grid beginner helper (unchanged) ────────────────────────

export type GridSuggestion = {
  symbol: string;
  market: 'spot';
  lowerPrice: number;
  upperPrice: number;
  gridLevels: number;
  rationale: string;
  currentPriceInRange: boolean;
  currentPrice: number | null;
  samples: number;
  degraded: boolean;
};

export function suggestGrid(args: {
  symbol: string;
  lookbackDays?: number;
}): Promise<GridSuggestion> {
  return authFetch('/api/strategy/grid/suggest', {
    method: 'POST',
    body: args,
  });
}

// ── Research-terminal: promote an LLM/user-authored strategy ──
//
// Posts an untrusted candidate StrategyDefinition to the promotion boundary
// (api-gateway → strategy-engine). The gateway gates on research tier and
// stamps approval; the engine validates + sanitizes (forces paper, strips
// liveConfig, owns createdBy/enabled). See docs/design/research-terminal.md.
//
// Unlike authFetch, this surfaces the structured validation payload on a 400
// so the research UI can show field-level issues instead of a flat string.

export type PromotionIssue = { path: string; message: string };

export type PromoteResult =
  | { ok: true; strategy: unknown }
  | { ok: false; status: number; error: string; issues?: PromotionIssue[] };

export async function promoteStrategy(candidate: unknown): Promise<PromoteResult> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch('/api/strategies/promote', {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(candidate),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // non-JSON body — fall through with a generic message below
  }

  if (res.ok) {
    return { ok: true, strategy: payload };
  }

  const body = (payload ?? {}) as { error?: string; issues?: PromotionIssue[] };
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Promotion failed (${res.status})`,
    ...(body.issues ? { issues: body.issues } : {}),
  };
}
